import React from 'react';
import * as Styled from './Impact.styled';
import { DEFAULT_FREQUENCIES, DEFAULT_ONE_TIME_AMOUNTS, DEFAULT_RECURRING_AMOUNTS, FALLBACK_TYPEGUARD_AMOUNT, FALLBACK_TYPEGUARD_FREQUENCY } from './Impact.constants';
import { setColorWithOpacityCss } from '../../utils/setColorWithOpacityCss';
import { FREQUENCY } from '../../models/DonationFrequency';
import { AMOUNT_INPUT, MAX_DONATION_AMOUNT, MIN_DONATION_AMOUNT } from '../../models/DonationAmount';
import { getVisibleObjects } from './utils/getVisibleObjects';
import { getDefaultObject } from './utils/getDefaultObject';
import { useImpactBlockReducer } from './useImpactBlockReducer';
import { DonationAmountRadioGroup } from './components/DonationAmountRadioGroup';
import { ImpactText } from './components/ImpactText';
import { DonationFrequencySelect } from './components/DonationFrequencySelect';
import { ProgramDesignationsSelect } from './components/ProgramDesignationsSelect/ProgramDesignationsSelect';
import { ImpactDonationAmount } from './components/ImpactDonationAmount/ImpactDonationAmount';
import { ImpactImage } from './components/ImpactImage';
import { ErrorMessage } from './components/ErrorMessage';
import { CallToActionButton } from './components/CallToActionButton';
import { FrequencyPreHeader } from './components/FrequencyPreHeader';
import { formatCurrency } from '../../features/passport/formatCurrency';
import { trimAmountForCurrency } from '../../features/passport/trimAmountForCurrency';
import { useLoadingState } from '../../hooks/useLoadingState';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * The `Impact` content block allows a donor to direct their one-time or recurring donation to a
 * specific program set up by the organization.
 */
export const Impact = _ref => {
  let {
    cssColor,
    cssBackgroundColor,
    cssBackgroundColorOpacity,
    cssTextAlign,
    alignXPreset,
    headingContent = '',
    descriptionContent = '',
    donationAmounts = DEFAULT_ONE_TIME_AMOUNTS,
    recurringAmounts = DEFAULT_RECURRING_AMOUNTS,
    donationFrequencies = DEFAULT_FREQUENCIES,
    programDesignation,
    hideRecurringEndDate = true,
    imageUrl,
    imageAltText,
    imagePosition,
    cssImageOpacity,
    blockId,
    parentLayoutInfo,
    campaignId = '',
    checkoutBaseUrl,
    forwardCheckoutQueryParams,
    programDesignations = {},
    isCartEnabled = false,
    minDonationAmount: rawMinDonationAmount = MIN_DONATION_AMOUNT,
    maxDonationAmount = MAX_DONATION_AMOUNT,
    NextImage,
    orgId = '',
    fitVertical = false,
    currencyCode = 'USD',
    isLoading = false,
    campaignLocale = 'en-US',
    ...rest
  } = _ref;
  const {
    loadingState,
    resetLoadingStateOnAnimationEnd
  } = useLoadingState(isLoading);
  const otherAmountInputRef = React.useRef(null);
  const visibleFrequencies = getVisibleObjects(donationFrequencies);
  const visibleRecurringFrequencies = visibleFrequencies.filter(frequency => frequency.name !== FREQUENCY.ONE_TIME);

  /*
   * The Impact Block requires a initialFrequency. If no visible frequency is set to be default, we use a fallback frequency.
   * This mostly acts as a typeguard, but also would prevent app crashes that might result from using type assertions.
   */
  const initialFrequency = getDefaultObject(visibleFrequencies) ?? FALLBACK_TYPEGUARD_FREQUENCY;
  const visibleOneTimeAmounts = getVisibleObjects(donationAmounts);
  const visibleRecurringAmounts = getVisibleObjects(recurringAmounts);
  const defaultOneTimeAmount = getDefaultObject(visibleOneTimeAmounts) || visibleOneTimeAmounts[0] || FALLBACK_TYPEGUARD_AMOUNT;
  const defaultRecurringAmount = getDefaultObject(visibleRecurringAmounts) || visibleRecurringAmounts[0] || FALLBACK_TYPEGUARD_AMOUNT;

  /* The Impact Block requires a initialAmount. If no visibleAmount is configured as default, we use the fallback amount */
  const initialAmount = initialFrequency.name === FREQUENCY.ONE_TIME ? defaultOneTimeAmount : defaultRecurringAmount;

  // * If block is not using program designations, this will always be an empty array
  const blockProgramDesignations = programDesignations?.[blockId] ?? [];
  const hasSingleProgramDesignation = blockProgramDesignations.length === 1;
  const hasMoreThanOneProgramDesignation = blockProgramDesignations.length > 1;

  /*
   * If block allows more than one program designation, but has no default set;
   * initialProgramDesignation will remain undefined
   */
  let initialProgramDesignation;

  // * If block only allows one program designation, this will be the designation
  if (hasSingleProgramDesignation) {
    ;
    [initialProgramDesignation] = blockProgramDesignations;
  }

  // * If block allows more than one program designation, and has a default set, this will be the default pd
  if (hasMoreThanOneProgramDesignation) {
    initialProgramDesignation = getDefaultObject(blockProgramDesignations);
  }
  const INITIAL_BLOCK_STATE = React.useMemo(() => ({
    frequency: initialFrequency.name,
    amountNumber: initialAmount?.amount ?? 0,
    defaultOneTimeAmount,
    defaultRecurringAmount,
    currencyCode,
    locale: campaignLocale,
    activeDonationAmountName: initialAmount.name,
    otherAmountDisplayValue: '',
    recurringEndDate: '',
    programDesignation: initialProgramDesignation,
    isOtherAmountInputTouched: false,
    isRecurringEndDateSelectTouched: false,
    isProgramDesignationSelectTouched: false,
    isCallToActionButtonTouched: false
  }), [currencyCode, defaultOneTimeAmount, defaultRecurringAmount, initialAmount?.amount, initialAmount.name, initialFrequency.name, initialProgramDesignation, campaignLocale]);
  const [blockState, dispatch] = useImpactBlockReducer(INITIAL_BLOCK_STATE);

  /**
   * Convert min donation amount to match the locale's precision (prevents comparision errors
   * when comparing the other amount to the min donation amount)
   */
  const minDonationAmount = trimAmountForCurrency(rawMinDonationAmount, blockState.currencyCode, blockState.locale);
  const isOtherAmountInputErrored = blockState.amountNumber === null || blockState.amountNumber < minDonationAmount;
  const isRecurringEndDateErrored = blockState.recurringEndDate !== '' && new Date(blockState.recurringEndDate) <= new Date();

  // ! This will be undefined if a program designation group is used but no option is selected
  const isProgramDesignationSelectErrored = !blockState?.programDesignation;
  const amounts = blockState.frequency === FREQUENCY.ONE_TIME ? visibleOneTimeAmounts : visibleRecurringAmounts;
  const isOtherAmountAvailableForSelectedFrequency = Boolean(amounts.find(amount => amount.name === AMOUNT_INPUT.OTHER));
  const shouldShowPreHeader =
  // There is only one frequency, and that frequency is a recurring frequency
  visibleFrequencies.length === 1 && visibleRecurringFrequencies.length === 1;
  let amountInputValue = blockState.otherAmountDisplayValue;
  if (blockState.activeDonationAmountName !== AMOUNT_INPUT.OTHER) {
    // Format the preset value (we don't format otherAmountDisplayValue because it is already formatted)
    amountInputValue = formatCurrency({
      amount: String(blockState.amountNumber),
      locale: blockState.locale,
      currencyCode: blockState.currencyCode,
      options: {
        currencyDisplay: 'code',
        trailingZeroDisplay: 'stripIfInteger'
      }
    }).replace(currencyCode, '').trim();
  }

  /*
   * The INITIAL_BLOCK_STATE is derived from this component's props and is recreated if relevant
   * props change. If INITIAL_BLOCK_STATE is updated, we reset the component's internal blockState
   * to the new INITIAL_BLOCK_STATE. This allows consumers of the component to update the visual
   * state of the block correctly via prop value changes.
   */
  React.useEffect(() => {
    // A reducer's initial state cannot be changed after creation and must be manually dispatched
    dispatch({
      type: 'SET_ENTIRE_BLOCK_STATE',
      newBlockState: INITIAL_BLOCK_STATE
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps -- don't need dispatch in useEffect dependency array
  }, [INITIAL_BLOCK_STATE]);
  return /*#__PURE__*/_jsx(Styled.ImpactStyledContentContainer, {
    "data-id": "cp-impact",
    parentLayoutInfo: parentLayoutInfo,
    fitVertical: fitVertical,
    onAnimationEnd: resetLoadingStateOnAnimationEnd,
    children: /*#__PURE__*/_jsxs(Styled.ImpactContainer, {
      alignXPreset: alignXPreset,
      isTransparent: cssBackgroundColorOpacity === 0,
      isUsingImage: Boolean(imageUrl),
      parentLayoutInfo: parentLayoutInfo,
      fitVertical: fitVertical,
      ...rest,
      children: [imageUrl && /*#__PURE__*/_jsx(ImpactImage, {
        cssImageOpacity: cssImageOpacity,
        src: imageUrl,
        alt: imageAltText,
        imagePosition: imagePosition,
        parentLayoutInfo: parentLayoutInfo,
        NextImage: NextImage,
        priority: false
        // mobile first height
        ,
        height: "200"
      }), /*#__PURE__*/_jsxs(Styled.Form, {
        isUsingImage: Boolean(imageUrl),
        cssBackgroundColor: setColorWithOpacityCss(cssBackgroundColor, cssBackgroundColorOpacity),
        parentLayoutInfo: parentLayoutInfo,
        onSubmit: () => false,
        fitVertical: fitVertical,
        children: [shouldShowPreHeader && /*#__PURE__*/_jsx(FrequencyPreHeader
        // Don't show frequency if ihe frequency is one-time
        , {
          frequency: blockState.frequency,
          cssTextAlign: cssTextAlign
        }), (headingContent || descriptionContent) && /*#__PURE__*/_jsx(ImpactText, {
          headingContent: headingContent,
          descriptionContent: descriptionContent,
          cssColor: cssColor,
          cssTextAlign: cssTextAlign
        }), /*#__PURE__*/_jsx(ErrorMessage, {
          blockId: blockId,
          fitVertical: fitVertical,
          minDonationAmount: formatCurrency({
            amount: minDonationAmount,
            locale: blockState.locale,
            currencyCode: blockState.currencyCode,
            options: {
              trailingZeroDisplay: 'stripIfInteger'
            }
          }),
          showErrorSpaceWhenNoVisibleErrors: Boolean(headingContent || descriptionContent),
          showOtherAmountInputErrorMessage: isOtherAmountInputErrored && (blockState.isOtherAmountInputTouched || blockState.isCallToActionButtonTouched),
          showRecurringEndDateErrorMessage: isRecurringEndDateErrored && blockState.isRecurringEndDateSelectTouched,
          showProgramDesignationErrorMessage: isProgramDesignationSelectErrored && (blockState.isProgramDesignationSelectTouched || blockState.isCallToActionButtonTouched)
        }), /*#__PURE__*/_jsx(ImpactDonationAmount, {
          isEditable: isOtherAmountAvailableForSelectedFrequency
          // 'value' is the single source of truth for what the Donation Amount will display
          ,
          value: amountInputValue,
          locale: blockState.locale,
          currencyCode: blockState.currencyCode,
          maxDonationAmount: maxDonationAmount,
          inputRef: otherAmountInputRef,
          dispatch: dispatch,
          disabled: !isOtherAmountAvailableForSelectedFrequency,
          blockId: blockId,
          frequency: blockState.frequency,
          shouldForceFocusStyling:
          // is the only available amount "other"
          isOtherAmountAvailableForSelectedFrequency && amounts.length === 1 ||
          // the block defaults to "other" as selected but the other amount input has not yet been interacted with
          initialAmount.name === AMOUNT_INPUT.OTHER && blockState.activeDonationAmountName === AMOUNT_INPUT.OTHER && !blockState.isOtherAmountInputTouched,
          shouldShowErrorState: isOtherAmountInputErrored && (blockState.isOtherAmountInputTouched || blockState.isCallToActionButtonTouched),
          loadingState: loadingState
        }), amounts.length > 1 && /*#__PURE__*/_jsx(DonationAmountRadioGroup, {
          amounts: amounts,
          activeDonationAmountName: blockState.activeDonationAmountName,
          locale: blockState.locale,
          currencyCode: blockState.currencyCode,
          otherAmountInputRef: otherAmountInputRef,
          frequency: blockState.frequency,
          dispatch: dispatch,
          loadingState: loadingState
        }), (visibleFrequencies.length || hasMoreThanOneProgramDesignation) && /*#__PURE__*/_jsxs(Styled.DonationFrequencyAndDesignationsContainer, {
          children: [visibleFrequencies.length > 0 && /*#__PURE__*/_jsx(DonationFrequencySelect, {
            value: blockState.frequency,
            options: visibleFrequencies.map(frequency => frequency.name),
            dispatch: dispatch,
            blockId: blockId,
            shouldShowErrorState: isRecurringEndDateErrored && blockState.isRecurringEndDateSelectTouched,
            hideRecurringEndDate: hideRecurringEndDate || blockState.frequency === FREQUENCY.ONE_TIME,
            recurringEndDate: blockState.recurringEndDate,
            hideDonationFrequencySelect: visibleFrequencies.length < 2
          }), hasMoreThanOneProgramDesignation && /*#__PURE__*/_jsx(ProgramDesignationsSelect, {
            value: blockState.programDesignation,
            options: blockProgramDesignations,
            dispatch: dispatch,
            blockId: blockId,
            shouldShowErrorState: isProgramDesignationSelectErrored && (blockState.isProgramDesignationSelectTouched || blockState.isCallToActionButtonTouched)
          })]
        }), /*#__PURE__*/_jsx(CallToActionButton, {
          blockState: blockState,
          campaignId: campaignId,
          checkoutBaseUrl: checkoutBaseUrl,
          forwardCheckoutQueryParams: forwardCheckoutQueryParams,
          isCartEnabled: isCartEnabled,
          orgId: orgId,
          error: isOtherAmountInputErrored && blockState.isOtherAmountInputTouched || isProgramDesignationSelectErrored && blockState.isProgramDesignationSelectTouched || isRecurringEndDateErrored && blockState.isRecurringEndDateSelectTouched,
          dispatch: dispatch,
          isLoading: isLoading
        })]
      })]
    })
  });
};